import CDSFeedDom from "./cds-feed.module.dom";
import { $elements, $elementChild } from "@grindex-common/helpers/elements";
import { Loader } from "@grindex-components/Loader/loader.class";
import { events } from "@grindex-common/helpers/events";
import TabNames from "@grindex-client/components/Tabs/TabNames.class";
import asyncModule from "@grindex-client/components/Module/asyncModule";
import { z } from "zod";

(function listenForEvent() {
	asyncModule("cds-feed", events.tabLoadedEvent(new TabNames().product.drawings), init);
})();

function init($module) {
	if (!$module) {
		return;
	}

	let productId = $module.dataset.currentProduct;
	const brandId = $module.dataset.currentBrand;

	const dom = new CDSFeedDom($module);
	const URL_API_ROOT = "/api/cdsfeedapi/";
	const $loader = $elementChild($module, "loader");

	let currentPage = 0;
	let productData = [];
	let initial = true;

	const isUrl = (str) => {
		try {
			let url = str;
			if (url.startsWith("//")) {
				url = window.location.protocol + url;
			}

			/* eslint-disable no-new */
			new URL(url);
			return true;
		} catch (ex) {
			return false;
		}
	};

	const onRequest = {
		loader: new Loader($loader),
	};

	dom.getShowMoreButton().addEventListener("click", function () {
		if (dom.showMoreButtonIsEnabled()) {
			currentPage = currentPage + 1;
			filterSearch(false);
		}
	});

	filterSearch(true);

	function getSelectedFacetGroups() {
		const facetGroups = [];

		[...$elements("cds-feed-facet-group")].filter((element) => {
			const selectedFacets = [...dom.getFacets(element)].filter((facet) => facet.dataset.checked === "true");
			if (selectedFacets.length > 0) {
				facetGroups.push({
					groupName: element.dataset.facetId,
					values: selectedFacets.map((mappedFacet) => encodeURIComponent(mappedFacet.id)),
				});
			}
		});

		return facetGroups;
	}

	function getFacetsQuery() {
		let query = "&filter=";

		getSelectedFacetGroups().forEach((facetGroup, index) => {
			if (index !== 0) {
				query = `${query};`;
			}

			query = `${query}${facetGroup.groupName}:2:${facetGroup.values.join(":")}`;
		});

		return query;
	}

	function bindLinks() {
		dom.getProductLinks().forEach((link) => {
			link.addEventListener("click", getProduct);
		});
	}

	function bindFacets() {
		dom.getCollapsibles().forEach((collapsible) => {
			collapsible.addEventListener("click", dom.toggleFilter);
		});

		dom.getFacets().forEach(($facet) => {
			$facet.addEventListener("click", function () {
				dom.toggleFacetState($facet);
				filterSearch(true);
			});
		});
	}

	function hideSpinner() {
		onRequest.loader.hide();
	}

	function showSpinner() {
		onRequest.loader.show();
	}

	function storeProductData(product) {
		const productContainer = {
			id: product.id,
			attributes: [],
		};
		for (let i = 0; i < product.attributes.length; i++) {
			productContainer.attributes.push({
				id: product.attributes[i].label,
				value: product.attributeValues[i],
				visible: product.attributes[i].visible,
			});
		}

		productData.push(productContainer);
	}

	function clearResults() {
		dom.getVariantsContainer().innerHTML = "";
		dom.clearProductResult();
		productData = [];
		currentPage = 0;
	}

	function deselectAllProductLinks() {
		dom.getProductLinks().forEach(($link) => {
			$link.classList.remove("selected");
		});
	}

	function selectActiveLink($link) {
		$link.classList.add("selected");
	}

	function renderProductInfo(product) {
		[...product.attributes].forEach((attribute) => {
			if (!attribute.visible) {
				return;
			}
			const $container = document.getElementById("product-data");
			const $row = document.createElement("tr");
			const $idSpan = document.createElement("td");
			const $valueSpan = document.createElement("td");

			$idSpan.appendChild(document.createTextNode(attribute.id));
			if (attribute.value.startsWith("http")) {
				const pdfLink = document.createElement("a");
				pdfLink.href = attribute.value;
				pdfLink.innerHTML = attribute.value;
				pdfLink.target = "_blank";
				$valueSpan.appendChild(pdfLink);
			} else {
				$valueSpan.appendChild(document.createTextNode(attribute.value));
			}

			$row.appendChild($idSpan);
			$row.appendChild($valueSpan);

			$container.appendChild($row);
		});
	}

	function bindDownloadLinks() {
		const links = $elements("cds-download-link");
		links.forEach((link) => {
			link.addEventListener("click", getDownloadLink);
		});
	}

	function bindImageLinks() {
		dom.getImageProductLinks().forEach((imageLink) => {
			imageLink.addEventListener("click", (link) => {
				productId = link.target.dataset.productId;
				initial = true;
				filterSearch(true);
			});
		});
	}

	function checkPaging(data) {
		if (data.endRow >= data.rowCount) {
			dom.disableShowMoreButton();
		} else {
			dom.enableShowMoreButton();
		}
	}

	function setCrumbs(res) {
		dom.clearBreadCrumbs();

		[...res.crumbs].forEach((crumb) => {
			dom.appendBreadCrumb(crumb, res);
		});

		const $crumbLinks = $elements("cds-crumb-link");

		[...$crumbLinks].forEach(($link) => {
			if ($link.target !== $crumbLinks[$crumbLinks.length - 1]) {
				$link.addEventListener("click", (evt) => {
					initial = true;
					evt.preventDefault();
					productId = evt.target.dataset.crumbid;
					filterSearch(true);
				});
			}
		});

		if ($crumbLinks.length) {
			$crumbLinks[$crumbLinks.length - 1].href = "";
		}
	}

	function showHideImage(res) {
		if (res.searchImageUrl && res.searchImageUrl.length) {
			dom.showSearchImage();
			dom.setSearchImageSource(res.searchImageUrl);
		} else {
			dom.hideSearchImage();
		}
	}

	function filterSearch(clear) {
		showSpinner();

		if (clear) {
			clearResults();
		}

		const url = `${URL_API_ROOT}FilterSearch/?id=${productId}&page=${currentPage}&brand=${brandId}${getFacetsQuery()}`;

		dom.hideSearchImage();

		fetch(url, { method: "POST" })
			.then((response) => response.json())
			.then((result) => {
				if (result.groupId === "root" || (result.children && result.children.length)) {
					dom.appendRootResultMarkup(result);
					dom.hideShowMoreButton();
					dom.hideFacetsContainer();
				} else {
					dom.appendProductResultMarkup(result);
					dom.showShowMoreButton();
					dom.showFacetsContainer();
				}

				showHideImage(result);

				dom.refreshProductLinks();

				checkPaging(result);

				for (let i = 0; i < result.products.length; i++) {
					storeProductData(result.products[i]);
				}

				bindLinks();
				bindImageLinks();

				if (initial) {
					dom.setFacetContainerMarkup(result);

					bindFacets();

					initial = false;
				}

				setCrumbs(result);
			})
			.catch((err) => {
				throw new Error("api fetch error, response: " + err);
			})
			.finally(() => {
				hideSpinner();
			});
	}

	function getProduct(evt) {
		showSpinner();
		evt.preventDefault();

		const id = evt.target.dataset.productid;
		const brand = evt.target.dataset.brand;
		const url = `${URL_API_ROOT}GetDocuments/?brand=${brand}&id=${id}`;

		deselectAllProductLinks();

		fetch(url, { method: "GET" })
			.then((response) => response.json())
			.then((res) => {
				deselectAllProductLinks();

				dom.setProductResultMarkup(res);

				const productInfo = productData.filter((info) => info.id === res.productID)[0];
				renderProductInfo(productInfo);

				selectActiveLink(evt.target);
				bindDownloadLinks();
			})
			.catch((err) => {
				throw new Error("api fetch error, response: " + err);
			})
			.finally(() => {
				hideSpinner();
			});
	}

	function waitForMS4(fileGuid) {
		
		const jobIdSchema = z.guid({ error: "Invalid job identifier" });
		const result = jobIdSchema.safeParse(fileGuid);
		if (!result.success) {			
			throw new Error("Invalid job identifier");
		}
		// Encode to avoid injection
		var safeGuid = encodeURIComponent(result.data);

		jQuery.ajax({
			url: `https://www.product-config.net/visualization/service/jobs/${safeGuid}?callback=?`,
			dataType: "jsonp",
			context: this,
			success: function (data) {
				if (data.job.resultUrl || data.job.errorText) {
					downloadFileAndMakePanelAvailable(data.job.resultUrl);
					return;
				} else {
					var timeout = setTimeout(function () {
						waitForMS4(fileGuid);
						clearTimeout(timeout);
					}, 1000);
				}
			},
		});
	}

	function downloadFileAndMakePanelAvailable(downloadUrl) {
		download(downloadUrl);
		dom.toggleDownloadLinks();
		hideSpinner();
	}

	function getDownloadLink(evt) {
		showSpinner();
		dom.toggleDownloadLinks();
		evt.preventDefault();

		const id = evt.target.dataset.productId;
		const brand = evt.target.dataset.brand;
		const format = evt.target.dataset.cadFormat;
		const url = `${URL_API_ROOT}GetDownloadLink/?id=${id}&brand=${brand}&format=${format}`;

		fetch(url, { method: "POST" })
			.then((response) => response.json())
			.then((result) => {
				if (isUrl(result.url)) {
					downloadFileAndMakePanelAvailable(result.url);
				} else {
					waitForMS4(result.url);
				}
			});
	}

	/** @deprecated Use `downloadFile` in the `files` module instead. */
	function download(path) {
		const documentLink = dom.createDocumentLink(path);

		document.body.appendChild(documentLink);

		documentLink.click();

		document.body.removeChild(documentLink);
	}
}

export default {
	view: "cds-feed",
	init,
};
